import React from 'react';
import { motion } from 'framer-motion';
import { EnrichedRiskArea } from '../../types/prevention';
import { RISK_LEVEL_CONFIG, STATUS_CONFIG, getRiskBand, FACTOR_CONFIG, FACTOR_SEVERITY_COLORS, RiskFactorScores, FactorSeverity } from '../RiskAreasWidget';
import { ChevronRight, MapPin, Users, AlertTriangle, Clock, CheckCircle2 } from 'lucide-react';
import { SifBadge, DataConfidenceBadge, ScheduledDateBadge, FreshnessBadge, hasDataGap } from './shared';
interface Props {
  areas: EnrichedRiskArea[];
  onSelect: (area: EnrichedRiskArea) => void;
}
export function VariantBList({
  areas,
  onSelect
}: Props) {
  if (areas.length === 0) return <div className="flex flex-col items-center justify-center h-full text-center py-16" data-id="element-2855" data-mp="2gyt1b2at9f6u">
        <CheckCircle2 size={48} className="text-emerald-400 mb-4" data-id="element-2856" data-mp="3edyo18puccg1" />
        <p className="text-lg font-medium text-gray-900" data-id="element-2857" data-mp="3ef9ph1vtd83z">No activities found</p>
      </div>;
  return <div className="divide-y divide-gray-100" data-id="element-2858" data-mp="39l5hqgeq4w1u">
      {areas.map((area, index) => {
      const config = RISK_LEVEL_CONFIG[area.riskLevel];
      const statusConfig = STATUS_CONFIG[area.status];
      const StatusIcon = statusConfig.icon;
      const band = getRiskBand(area.riskScore);
      const activeFactors = (Object.entries(area.riskFactorScores) as [keyof RiskFactorScores, FactorSeverity][]).filter(([_, severity]) => severity !== 'none').sort((a, b) => {
        const order = {
          high: 0,
          medium: 1,
          low: 2,
          none: 3
        };
        return order[a[1]] - order[b[1]];
      }).slice(0, 3);
      const dataGap = hasDataGap(area);
      let rowBg = 'hover:bg-gray-50';
      if (area.sifPotential) {
        rowBg = 'bg-red-50/60 hover:bg-red-50 border-l-4 border-red-700';
      } else if (dataGap) {
        rowBg = 'bg-amber-50/30 hover:bg-amber-50/50 border-l-4 border-dashed border-amber-400';
      } else if (area.status === 'closed') {
        rowBg = 'bg-emerald-50/30 opacity-75 hover:bg-emerald-50/50 border-l-4 border-transparent';
      } else if (area.status === 'measures_taken') {
        rowBg = 'bg-amber-50/30 hover:bg-amber-50/50 border-l-4 border-transparent';
      } else {
        rowBg += ' border-l-4 border-transparent';
      }
      return <motion.button key={area.id} initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} transition={{
        delay: index * 0.02
      }} onClick={() => onSelect(area)} className={`w-full text-left px-6 py-3.5 flex items-center gap-4 transition-colors group ${rowBg}`} data-id="element-2859" data-mp="3kjir3hg9tug2" data-mpi={index}>
            {/* Risk score band */}
            <div className={`flex-shrink-0 w-14 py-1.5 rounded-md ${config.bgColor} flex flex-col items-center`} data-id="element-2860" data-mp="38ss1jgbxtyh">
              <span className={`text-xs font-bold ${config.color}`} data-id="element-2861" data-mp="an22be6h4j2i">
                {band.short}
              </span>
              <span className={`text-[9px] ${config.color} opacity-70`} data-id="element-2862" data-mp="kj4zs6qdrtj4">
                {area.riskScore}
              </span>
            </div>

            {/* Location info */}
            <div className="flex-1 min-w-0" data-id="element-2863" data-mp="oi5izqcrsy6n">
              <div className="flex items-center gap-1.5 mb-0.5" data-id="element-2864" data-mp="tijrtam74c5q">
                {area.sifPotential && <div className="mr-1" data-id="element-2865" data-mp="yhd6h6d7y97h">
                    <SifBadge data-id="element-2866" data-mp="ygbjc3ixhw9e" />
                  </div>}
                <span className="text-sm font-semibold text-gray-900 truncate" data-id="element-2867" data-mp="139wiw0066yap">
                  {area.project}
                </span>
                <ChevronRight size={12} className="text-gray-300 flex-shrink-0" data-id="element-2868" data-mp="1ca2ufgcb5o78" />
                <span className="text-sm text-gray-600 truncate" data-id="element-2869" data-mp="1h8dzzevklcjn">
                  {area.floor}
                </span>
              </div>
              <div className="flex items-center gap-2" data-id="element-2870" data-mp="13km07nr3h8er">
                <p className="text-xs text-gray-400 truncate" data-id="element-2871" data-mp="zln0chk801u6">
                  {area.location}
                </p>
                <ScheduledDateBadge date={area.scheduledDate} data-id="element-2872" data-mp="1dax8tnlq45rz" />
              </div>
            </div>

            {/* Status */}
            <div className="flex items-center gap-2 flex-shrink-0 w-28" data-id="element-2873" data-mp="18f8epzsh3h32">
              <span className={`flex items-center gap-1 text-[10px] font-medium px-2 py-0.5 rounded ${statusConfig.bgColor} ${statusConfig.color}`} data-id="element-2874" data-mp="kocjstsq05rl">
                <StatusIcon size={10} data-id="element-2875" data-mp="ums86p0d50xe" />
                {statusConfig.label}
              </span>
            </div>

            {/* Factors */}
            <div className="hidden lg:flex items-center gap-1.5 flex-shrink-0 w-48" data-id="element-2876" data-mp="poqs8cy8hhq5">
              {activeFactors.map(([factor, severity]) => {
            const factorConf = FACTOR_CONFIG[factor];
            const sevColors = FACTOR_SEVERITY_COLORS[severity];
            return <div key={factor} title={factorConf.description} className={`flex items-center gap-1 px-2 py-0.5 rounded-full cursor-help ${sevColors.bg}`} data-id="element-2877" data-mp="3j1k6jwwkxyai">
                    <span className={`w-1.5 h-1.5 rounded-full ${sevColors.dot}`} data-id="element-2878" data-mp="3excf3ryqm48l" />
                    <span className={`text-[10px] font-medium ${sevColors.text}`} data-id="element-2879" data-mp="6jc4urb3xaz0">
                      {factorConf.label}
                    </span>
                  </div>;
          })}
            </div>

            {/* Freshness & Data Quality */}
            <div className="hidden xl:flex items-center gap-3 flex-shrink-0 justify-end" data-id="element-2880" data-mp="1prhrub1y1el">
              <DataConfidenceBadge confidence={area.dataConfidence} data-id="element-2881" data-mp="pg50uvbnspx1" />
              <FreshnessBadge lastUpdated={area.lastUpdated} data-id="element-2882" data-mp="u85715ibof22" />
            </div>

            <ChevronRight size={16} className="text-gray-300 group-hover:text-gray-400 transition-colors flex-shrink-0 ml-2" data-id="element-2883" data-mp="143lvk6vliyt6" />
          </motion.button>;
    })}
    </div>;
}